<template>
  <div class="subproperty-form-page">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/' }">
        <app-i18n code="home.menu"></app-i18n>
      </el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: '/subproperty/list' }">
        <app-i18n code="entities.subproperty.menu"></app-i18n>
      </el-breadcrumb-item>
      <el-breadcrumb-item>
        <app-i18n code="entities.subproperty.edit.title" v-if="isEditing"></app-i18n>
        <app-i18n code="entities.subproperty.new.title" v-if="!isEditing"></app-i18n>
      </el-breadcrumb-item>
    </el-breadcrumb>

    <!-- Back button -->
    <app-back-button
      route-name="subpropertyList"
      :i18nCode="!isPropertyNBA ? `entities.subproperty.label` : 'entities.base.label'"
    />

    <!-- Title -->
    <h1 class="app-content-title app-content-title--hidden">
      <app-i18n
        v-if="isEditing"
        :code="!isPropertyNBA ? 'entities.subproperty.edit.title' : 'entities.base.edit.title'"
      ></app-i18n>
      <app-i18n
        v-if="!isEditing"
        :code="!isPropertyNBA ? 'entities.subproperty.new.title' : 'entities.base.new.title'"
      ></app-i18n>
    </h1>

    <!-- Content -->
    <div class="app-content-page">
      <div class="app-page-spinner" v-if="findLoading" v-loading="findLoading"></div>

      <el-form
        v-loading="saveLoading"
        v-if="model"
        :label-position="labelPosition"
        :label-width="labelWidthForm"
        :model="model"
        :rules="rules"
        @submit.native.prevent="doSubmit"
        class="form"
        ref="form"
      >
        <!-- Subtitle -->
        <h2 class="app-content-subtitle">
          <app-i18n code="entities.subproperty.form.subtitle.information" />
        </h2>

        <!-- Section 1 -->
        <el-row :gutter="10">
          <!-- Country -->
          <el-col :lg="6" :md="12" :sm="24">
            <el-form-item
              :label="fields.country.label"
              :prop="fields.country.name"
              :required="fields.country.required"
            >
              <el-select
                v-model="model[fields.country.name]"
                :disabled="disableCountry"
                :placeholder="i18n('entities.subproperty.placeholders.country')"
              >
                <i class="icon-location" slot="prefix"></i>
                <el-option
                  v-for="option in supportedCountries"
                  :key="option.id"
                  :label="option.label"
                  :value="option.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>

          <!-- Region -->
          <el-col :lg="6" :md="12" :sm="24" v-if="showRegion">
            <el-form-item
              :label="fields.region.label"
              :prop="fields.region.name"
              :required="fields.region.required"
            >
              <el-input
                v-model="model[fields.region.name]"
                prefix-icon="icon-location"
                :placeholder="i18n('entities.subproperty.placeholders.region')"
                ref="focus"
              />
            </el-form-item>
          </el-col>

          <!-- Name -->
          <el-col :lg="12" :md="24" :sm="24">
            <el-form-item
              :label="!isPropertyNBA ? fields.name.label : i18n('entities.base.fields.name')"
              :prop="fields.name.name"
              :required="fields.name.required"
            >
              <el-input
                v-model="model[fields.name.name]"
                prefix-icon="icon-cup"
                :placeholder="!isPropertyNBA ? i18n('entities.subproperty.placeholders.name') : i18n('entities.base.fields.name')"
                ref="focus"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Separator -->
        <div class="app-content-separator" />

        <!-- Subtitle -->
        <h2 class="app-content-subtitle subproperty-form-page__subtitle">
          <app-i18n code="entities.subproperty.form.subtitle.management" />
        </h2>

        <!-- Description -->
        <h3 class="app-content-description subproperty-form-page__description">
          <app-i18n code="entities.subproperty.form.description.email" />
        </h3>

        <!-- Section 2 -->
        <el-row :gutter="10">
          <!-- Emails -->
          <el-col :lg="24" :md="24" :sm="24">
            <el-form-item
              :label="fields.emails.label"
              :prop="fields.emails.name"
              :required="fields.emails.required"
            >
              <emails-input
                v-model="model[fields.emails.name]"
                prefix-icon="icon-mail"
                :placeholder="i18n('entities.subproperty.placeholders.email')"
              ></emails-input>
            </el-form-item>
          </el-col>
          <el-col :offset="12" />

          <!-- Status -->
          <el-col :lg="12" :md="24" :sm="24">
            <el-form-item
              v-if="isEditing"
              :label="fields.status.label"
              :prop="fields.status.name"
              :required="fields.status.required"
            >
              <el-select
                v-model="model[fields.status.name]"
                :placeholder="i18n('entities.subproperty.placeholders.email')"
              >
                <i :class="prefixStatus" slot="prefix"></i>
                <el-option
                  v-for="option in fields.status.options"
                  :key="option.id"
                  :label="option.label"
                  :value="option.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Payments Enabled -->
        <el-row :gutter="10">
          <el-col
            v-if="hasPermissionToAuditLogs && !isPropertyNBA"
            :lg="24" :md="24" :sm="24"
          >
            <el-form-item
              class="subproperty-form-page__payments"
              :prop="fields.paymentsEnabled.name"
              :required="fields.paymentsEnabled.required"
            >
              <el-checkbox v-model="model[fields.paymentsEnabled.name]">
                <img
                  :class="{ disabled: !model[fields.paymentsEnabled.name] }"
                  src="/images/stripe.png"
                />
                <span v-text="fields.paymentsEnabled.label" />
              </el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Owqlo fees -->
        <el-row :gutter="10" v-if="enableOwqloFees">
          <el-col :lg="6" :md="8" :sm="12">
            <el-form-item
              :label="fields.owqloFeeFixed.label"
              :prop="fields.owqloFeeFixed.name"
              :required="fields.owqloFeeFixed.required"
            >
              <el-input
                prefix-icon="icon-card"
                :placeholder="fields.owqloFeeFixed.label"
                ref="focus"
                type="number"
                v-model.number="model[fields.owqloFeeFixed.name]"
              />
            </el-form-item>
          </el-col>

          <el-col :lg="5" :md="8" :sm="12">
            <el-form-item
              :label="fields.owqloFeePercentage.label"
              :prop="fields.owqloFeePercentage.name"
              :required="fields.owqloFeePercentage.required"
            >
              <el-input
                prefix-icon="icon-percent"
                :placeholder="fields.owqloFeePercentage.label"
                ref="focus"
                type="number"
                v-model.number="model[fields.owqloFeePercentage.name]"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Separator -->
        <div class="app-content-separator" />

        <!-- Subtitle -->
        <h2 class="app-content-subtitle">
          <app-i18n code="entities.subproperty.form.subtitle.settings" />
        </h2>

        <!-- Settings -->
        <el-row :gutter="10">
          <!-- Birthdate type -->
          <el-col :lg="12" :md="24" :sm="24">
            <el-form-item
              :label=" fields.birthdateType.label"
              :prop="fields.birthdateType.name"
              :required="fields.birthdateType.required"
            >
              <el-select
                :placeholder="i18n('common.select.placeholder')"
                v-model="model[fields.birthdateType.name]"
                :disabled="isEditing"
              >
                <i slot="prefix" class="icon-cup" />
                <el-option
                  :key="option.id"
                  :label="option.label"
                  :value="option.id"
                  v-for="option in fields.birthdateType.options"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>

          <!-- Date format -->        
          <el-col :lg="6" :md="12" :sm="12">
            <el-form-item
              :label="fields.dateFormat.label"
              :prop="fields.dateFormat.name"
              :required="fields.dateFormat.required"
            >
              <el-select
                :placeholder="i18n('common.select.placeholder')"
                v-model="model[fields.dateFormat.name]"
              >
                <i slot="prefix" class="icon-calendar" />
                <el-option
                  :key="option.id"
                  :label="option.label"
                  :value="option.id"
                  v-for="option in fields.dateFormat.options"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>

          <!-- Time format -->
          <el-col :lg="6" :md="12" :sm="12">
            <el-form-item
              :label="fields.timeFormat.label"
              :prop="fields.timeFormat.name"
              :required="fields.timeFormat.required"
            >
              <el-select
                :placeholder="i18n('common.select.placeholder')"
                v-model="model[fields.timeFormat.name]"
              >
                <i slot="prefix" class="icon-clock" />
                <el-option
                  :key="option.id"
                  :label="option.label"
                  :value="option.id"
                  v-for="option in fields.timeFormat.options"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Form controls -->
        <el-form-item>
          <div class="subproperty-form-page__form-buttons form-buttons">
            <router-link :to="{name: 'subpropertyList'}">
              <el-button type="secondary">
                <app-i18n code="common.back.default" />
              </el-button>
            </router-link>

            <el-button class="right" :disabled="saveLoading" @click="doSubmit" type="primary">
              <app-i18n v-if="!isEditing" code="common.create" />
              <app-i18n v-if="isEditing" code="common.save" />
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import { i18n } from '@shared/i18n';

import { FormSchema } from '@shared/form/form-schema';

import { SubpropertyModel } from '@modules/subproperty/subproperty-model';
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';

import supportedCountries from '@enums/supportedCountries.json';
import { get, update } from 'lodash';

const { fields } = SubpropertyModel;

const formSchema = new FormSchema([
  fields.id,
  fields.propertyId,
  fields.name,
  fields.slug,
  fields.emails,
  fields.managers,
  fields.status,
  fields.access,
  fields.country,
  fields.region,
  fields.logo,
  fields.birthdateType,
  fields.dateFormat,
  fields.timeFormat,
  fields.paymentsEnabled,
  fields.owqloFeeFixed,
  fields.owqloFeePercentage,
]);

export default {
  name: 'app-subproperty-form-page',

  props: ['id'],

  data() {
    return {
      rules: formSchema.rules(),
      model: null,
    };
  },

  computed: {
    ...mapGetters({
      labelPosition: 'layout/labelPosition',
      labelWidthForm: 'layout/labelWidthForm',
      record: 'subproperty/form/record',
      findLoading: 'subproperty/form/findLoading',
      saveLoading: 'subproperty/form/saveLoading',
      currentUser: 'auth/currentUser',
      currentManage: 'auth/manage/currentManage',
      isPropertyNBA: 'auth/manage/isPropertyNBA',
    }),
    propertyCountry() {
      return get(
        this,
        'currentManage.property.country',
        null,
      );
    },
    isPropertyNBA() {
      return (
        get(this, 'currentManage.property.type', '') ===
        'nba'
      );
    },
    disableCountry() {
      if (this.isPropertyNBA) {
        update(
          this,
          'model.country',
          () => this.propertyCountry,
        );
      }
      return this.isPropertyNBA || this.isEditing;
    },
    showRegion() {
      return this.isPropertyNBA;
    },
    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },
    isEditing() {
      return !!this.id;
    },
    fields() {
      return fields;
    },
    supportedCountries() {
      return this.fields.country.options.filter(
        ({ id }) => {
          return supportedCountries.some(
            (countryId) => countryId === id,
          );
        },
      );
    },
    prefixStatus() {
      var status = '';
      if (this.model.status === 'enabled')
        return (status = 'icon-accept');
      if (this.model.status === 'disabled')
        return (status = 'icon-quit');
      return status;
    },
    enableOwqloFees() {
      return this.model[fields.paymentsEnabled.name] && !this.isPropertyNBA && this.hasPermissionToAuditLogs;
    },
  },

  async created() {
    if (this.isEditing) {
      await this.doFind(this.id);
    } else {
      await this.doNew();
    }
    this.model = formSchema.initialValues(this.record);
    this.model.logo = this.currentManage.property.logos;
  },

  methods: {
    ...mapActions({
      doFind: 'subproperty/form/doFind',
      doNew: 'subproperty/form/doNew',
      doUpdate: 'subproperty/form/doUpdate',
      doCreate: 'subproperty/form/doCreate',
    }),

    doReset() {
      this.model = formSchema.initialValues(this.record);
    },

    async doSubmit() {
      try {
        await this.$refs.form.validate();
      } catch (error) {
        return;
      }

      const { id, ...values } = formSchema.cast(this.model);

      if (this.isEditing) {
        return this.doUpdate({
          id,
          values,
        });
      } else {
        return this.doCreate(values);
      }
    },

    i18n(code) {
      return i18n(code);
    },
  },
};
</script>

<style lang="scss">
.subproperty-form-page {
  .el-form &__payments {
    .el-checkbox {
      display: flex;
      align-items: center;
    }

    .el-checkbox__label {
      display: flex;
      align-items: center;
      font-size: 14px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.29;
      letter-spacing: -0.3px;
      color: #333333;
    }

    .el-checkbox__inner {
      width: 20px;
      height: 20px;

      &::after {
        width: 4px;
        height: 12px;

        left: 6px;
        top: 0;
      }
    }

    img {
      width: 40px;
      height: 40px;
      margin-right: 8px;

      &.disabled {
        filter: grayscale(100%);
      }
    }
  }

  .app-content-separator {
    margin-top: 0;
  }
}
</style>

<style lang="scss" scoped>
.subproperty-form-page {
  &__subtitle {
    margin-bottom: 16px;
  }

  &__description {
    margin-bottom: 32px;
  }
}
</style>
